import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { merge, Observable, of, throwError } from 'rxjs';
import { map, catchError, filter, switchMap } from 'rxjs/operators';
import { IFileUploadService, UploadProgress } from './file-upload.service.interface';
import { environment } from "../../../../environments/environment";

@Injectable({
  providedIn: 'root',
})
export class FileUploadService implements IFileUploadService {
  constructor(private http: HttpClient) { }

  upload(fileToUpload: File): Observable<UploadProgress> {
    const initialProgress: UploadProgress = {
      progress: 0,
      state: 'PENDING',
      url: undefined,
    };

    // Step 1: Request the signed URL from your NestJS backend
    const getSignedUrl$ = this.http.post<any>(
      `${environment.baseUrl}s3/signed-upload`,
      {
        filename: fileToUpload.name,
        contentType: fileToUpload.type
      }
    );

    const upload$ = getSignedUrl$.pipe(
      switchMap((res) => {
        // These come from your S3Service.generateUploadUrl return object
        const { uploadUrl, fileUrl } = res;

        const headers = new HttpHeaders({
          'Content-Type': fileToUpload.type,
          // Tells AuthInterceptor to skip attaching our own Bearer token -
          // this PUT goes straight to S3's presigned URL, not our API.
          'X-Skip-Auth': 'true',
        });

        // Step 2: Perform the PUT request directly to S3
        return this.http.put(uploadUrl, fileToUpload, {
          headers,
          reportProgress: true,
          observe: 'events'
        }).pipe(
          filter(event =>
            event.type === HttpEventType.UploadProgress ||
            event.type === HttpEventType.Response
          ),
          map(event => {
            if (event.type === HttpEventType.UploadProgress) {
              return {
                progress: Math.round((100 * event.loaded) / (event.total || 1)),
                state: 'IN_PROGRESS',
              } as UploadProgress;
            }

            // Upload is complete
            return {
              progress: 100,
              state: 'DONE',
              url: fileUrl // This is the public https://... url
            } as UploadProgress;
          }),
          catchError(err => this.handleError(err))
        );
      }),
      catchError(err => this.handleError(err))
    );

    return merge(of(initialProgress), upload$);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Upload Error Context:', error);

    // If status is 403 and it's an OPTIONS request, it's a CORS/Bucket issue
    if (error.status === 403) {
      return throwError(() => 'S3 Access Denied: Check CORS policy or IAM permissions.');
    }

    if (error.status === 0) {
      return throwError(() => 'Network error: Check your S3 CORS configuration.');
    }

    return throwError(() => error.error?.message || 'Something went wrong during the upload.');
  }
}
